// PWA cold-start splash. Shows ONLY when the app is launched in standalone /
// installed mode (Add-to-Home-Screen, "Open in app" on desktop), and only on
// the first paint of the session — so navigation between routes doesn't
// re-trigger it. Animates in (~300ms), holds, then fades out at ~1s total.
import React, { useEffect, useState } from "react";

const SESSION_KEY = "flexx_pwa_splash_shown";

function isStandalone() {
  try {
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
    if (window.matchMedia && window.matchMedia("(display-mode: minimal-ui)").matches) return true;
    if (typeof window.navigator !== "undefined" && window.navigator.standalone) return true; // iOS Safari
  } catch (_) { /* noop */ }
  return false;
}

export default function PwaSplash() {
  const [phase, setPhase] = useState(() => {
    // 'hidden'  → never show
    // 'enter'   → fade/scale in
    // 'hold'    → fully visible
    // 'leave'   → fade out
    if (typeof window === "undefined") return "hidden";
    if (sessionStorage.getItem(SESSION_KEY)) return "hidden";
    if (!isStandalone()) return "hidden";
    return "enter";
  });

  useEffect(() => {
    if (phase === "hidden") return undefined;
    sessionStorage.setItem(SESSION_KEY, "1");
    // One-shot timeline — set up all timers once on mount.
    const t1 = setTimeout(() => setPhase("hold"), 250);
    const t2 = setTimeout(() => setPhase("leave"), 850);
    const t3 = setTimeout(() => setPhase("hidden"), 1200);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (phase === "hidden") return null;

  const visible = phase === "enter" || phase === "hold";
  const fullyIn = phase === "hold";

  return (
    <div
      data-testid="pwa-splash"
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        background: "#060606",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 18,
        opacity: visible ? 1 : 0,
        transition: "opacity 320ms ease",
        pointerEvents: visible ? "auto" : "none",
      }}
    >
      {/* Soft gold radial glow under the logo */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          width: 540,
          height: 540,
          borderRadius: "50%",
          background: "radial-gradient(closest-side, rgba(201,162,39,0.22), rgba(201,162,39,0) 70%)",
          filter: "blur(14px)",
          opacity: fullyIn ? 1 : 0.35,
          transition: "opacity 600ms ease",
        }}
      />
      <img
        src="/flexx-app-icon.png"
        alt="FLEXX ACADEMY"
        style={{
          position: "relative",
          width: "min(72vw, 420px)",
          height: "auto",
          filter: "drop-shadow(0 0 24px rgba(201,162,39,0.45))",
          transform: fullyIn ? "scale(1)" : "scale(0.92)",
          transition: "transform 480ms cubic-bezier(0.2, 0.7, 0.2, 1.05)",
        }}
        onError={(e) => { e.target.style.display = "none"; }}
      />
      <div
        style={{
          position: "relative",
          color: "#C9A227",
          fontFamily: "monospace",
          fontSize: 11,
          letterSpacing: 4,
          opacity: fullyIn ? 0.85 : 0,
          transition: "opacity 600ms ease 120ms",
        }}
      >
        STRUCTURED · TRADING · EDUCATION
      </div>
    </div>
  );
}
